import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/common';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleId: string;
  /**
   * Optional visible heading rendered at the top of the dialog box.
   * If omitted, the caller MUST render its own heading element with `id={titleId}`
   * somewhere inside `children` so `aria-labelledby` still resolves to text.
   */
  title?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Max-width of the dialog box. Tokens map 1:1 to Tailwind `max-w-*`.
   * Prefer `size` over putting `max-w-*` in `className`. (Both are reconciled
   * via `cn()`/tailwind-merge — caller `className` wins — but `size` is clearer.)
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /** Max-height utility for the dialog box. Default `max-h-[90vh]`. */
  maxHeightClass?: string;
  /** default true */
  closeOnBackdropClick?: boolean;
  /** default true */
  closeOnEscape?: boolean;
  /** Extra classes for the dialog box (NOT the backdrop). Merged via tailwind-merge, so these win over the defaults. */
  className?: string;
  ariaDescribedBy?: string;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared accessible modal wrapper.
 *
 * Features (FIX-9 / W11-final-audit A-1):
 * - role="dialog" + aria-modal="true" + aria-labelledby
 * - Focus on first focusable element when opened; restores previous focus on close
 * - Focus trap (Tab cycles within dialog)
 * - Escape closes
 * - Backdrop click closes (configurable)
 * - Renders via portal to document.body (avoids z-index nesting issues)
 */
export function Modal({
  isOpen,
  onClose,
  titleId,
  title,
  children,
  size = 'lg',
  maxHeightClass = 'max-h-[90vh]',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  ariaDescribedBy,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Save + restore focus.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Defer to next tick so children have actually mounted before we query focusables.
    const handle = window.setTimeout(() => {
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusables?.[0]?.focus();
    }, 0);

    return () => {
      window.clearTimeout(handle);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen]);

  // Escape closes.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap — Tab cycles within dialog.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[6px] flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={ariaDescribedBy}
        className={cn(
          'bg-card rounded-lg shadow-xl w-full overflow-hidden',
          SIZE_CLASSES[size],
          maxHeightClass,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h2 id={titleId} className="px-6 pt-6 pb-3 text-lg font-semibold text-foreground">
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
